import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { LoadingIcon } from '@/components/icons';
import { classnames, promiseWrapper } from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { buildBrowserInfo } from '@/helpers/googlepay';
import { useCardsContext } from '@/hooks/adyen/useCards';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { adyenServices } from '@/services';
import { CofPaymentData, NewOnlineCardPaymentData } from '@/types/adyen';
import { addPaymentCardFlowSchema } from '@/types/analytics';
import { PaymentCard } from '@/types/paymentcards';
import { PaymentData } from '@adyen/adyen-web/dist/types/components/types';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { ThreeDSComponent } from '../adyen';
import AddCard from '../adyen/AddCard';
import Modal from './redesign/Modal/Modal';

const baseTranslationKey = 'components.modules.modals.CreditCardModal';

type CreditCardModalProps = {
  isOpen: boolean;
  returnPath: string;
  amount?: number;
  guestId?: string;
  onCardAdded: (card: PaymentCard) => void;
  onBeforeThreeDSRedirect?: () => void;
  onAuthorize?: (paymentData: CofPaymentData) => void;
  onClose: () => void;
  isGiftcardCheckout?: boolean;
  traceTrackingSession?: {
    [key in keyof Omit<z.infer<typeof addPaymentCardFlowSchema>['properties'], 'step'>]: Omit<
      z.infer<typeof addPaymentCardFlowSchema>['properties'],
      'step'
    >[key];
  };
};

const CreditCardModal = ({
  isOpen,
  returnPath,
  guestId,
  amount,
  onClose,
  onCardAdded,
  onBeforeThreeDSRedirect,
  onAuthorize,
  isGiftcardCheckout,
  traceTrackingSession,
}: CreditCardModalProps) => {
  const [threeDSAction, setThreeDSAction] = useState<string>(null);
  const { loading, cards, waitForCard } = useCardsContext();
  const [view, updateView] = useState<'add' | 'pay'>('add');
  const { isMobileView } = useMobileView();
  const addCardView = guestId ? 'pay' : 'add';

  const handleOnAuthorize = async (paymentData: PaymentData) => {
    const paymentMethod = paymentData.paymentMethod;

    const parsedPaymentData: NewOnlineCardPaymentData = {
      type: 'newCard',
      encryptedCardNumber: paymentMethod.encryptedCardNumber,
      encryptedExpiryMonth: paymentMethod.encryptedExpiryMonth,
      encryptedExpiryYear: paymentMethod.encryptedExpiryYear,
      encryptedSecurityCode: paymentMethod.encryptedSecurityCode,
      holderName: paymentMethod.holderName,
    };

    const saveUserCardData: CofPaymentData = {
      paymentMethod: parsedPaymentData,
      redirectInfo: { returnPath },
      browserInfo: paymentData.browserInfo || buildBrowserInfo(),
    };

    trackMpEvent({
      name: 'add_payment_card_flow',
      properties: { step: 'verification', ...(traceTrackingSession ? traceTrackingSession : {}) },
    });

    if (onAuthorize) {
      onAuthorize(saveUserCardData);
      return;
    }

    const { data, error } = await promiseWrapper(adyenServices.saveUserCard({ paymentData: saveUserCardData }));

    if (data && data.requiresAction) {
      const type = JSON.parse(data?.adyenActionJson || '{}')?.type;
      if (type === 'redirect') onBeforeThreeDSRedirect?.();
      setThreeDSAction(data.adyenActionJson);
      return;
    }

    if (error) {
      toast(({ closeToast }) => <Snackbar type="success" label={_s('serverError')} onClose={closeToast} />);
    }

    const addedCard = await waitForCard(data?.paymentDetails?.additionalData?.['recurring.recurringDetailReference']);

    if (!addedCard) {
      toast(({ closeToast }) => (
        <Snackbar type="success" label={_s('checkoutError.StoreTokenError')} onClose={closeToast} />
      ));
      trackMpEvent({
        name: 'add_payment_card_flow',
        properties: { step: 'error', ...(traceTrackingSession ? traceTrackingSession : {}) },
      });
      return;
    }

    trackMpEvent({
      name: 'add_payment_card_flow',
      properties: { step: 'success', ...(traceTrackingSession ? traceTrackingSession : {}) },
    });

    onCardAdded(addedCard);
    onClose();
  };

  const handleOnAdditionalDetails = async (response) => {
    const { data, error } = await promiseWrapper(adyenServices.validate3DS({ details: response.data.details }));

    if (error || data?.resultCode !== 'Authorised') {
      toast(({ closeToast }) => (
        <Snackbar type="success" label={_s('checkoutError.StoreTokenError')} onClose={closeToast} />
      ));
    }

    setThreeDSAction(null);
    const addedCard = await waitForCard(data?.additionalData?.['recurring.recurringDetailReference']);

    if (!addedCard) {
      toast(({ closeToast }) => (
        <Snackbar type="success" label={_s('checkoutError.StoreTokenError')} onClose={closeToast} />
      ));
      trackMpEvent({
        name: 'add_payment_card_flow',
        properties: { step: 'error', ...(traceTrackingSession ? traceTrackingSession : {}) },
      });
      return;
    }

    trackMpEvent({
      name: 'add_payment_card_flow',
      properties: { step: 'success', ...(traceTrackingSession ? traceTrackingSession : {}) },
    });

    onCardAdded(addedCard);
    onClose();
  };

  const handleOnAbort = () => {
    trackMpEvent({
      name: 'add_payment_card_flow',
      properties: { step: 'cancel', ...(traceTrackingSession ? traceTrackingSession : {}) },
    });
    onClose();
  };

  const handleOnError = () => null;

  const title = _s(`${baseTranslationKey}.view.${view}.title`);
  const viewToShow = loading ? 'loading' : view;

  if (isOpen && viewToShow !== addCardView && !loading && !cards?.length) updateView(addCardView);

  useEffect(() => {
    if (!isOpen) return;
    trackMpEvent({
      name: 'add_payment_card_flow',
      properties: { step: 'initiated', ...(traceTrackingSession ? traceTrackingSession : {}) },
    });
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen}>
      <Modal.Content floating={!isMobileView}>
        <Modal.Header title={title} onClose={handleOnAbort} />
        <div data-cy="creditCardModal" className={classnames(isMobileView && 'px-lg')}>
          {viewToShow === 'loading' && <CardsLoading />}
          {(viewToShow === 'add' || viewToShow === 'pay') && (
            <>
              {threeDSAction && (
                <ThreeDSComponent
                  action={JSON.parse(threeDSAction)}
                  handleOnAdditionalDetails={handleOnAdditionalDetails}
                />
              )}
              {!threeDSAction && viewToShow === 'add' && (
                <AddCard
                  onAuthorize={handleOnAuthorize}
                  onError={handleOnError}
                  submitLabel={_s('addCard')}
                  disclaimerLabel={_s(`${baseTranslationKey}.view.${view}.disclaimer`)}
                />
              )}
              {!threeDSAction && viewToShow === 'pay' && (
                <AddCard
                  onAuthorize={handleOnAuthorize}
                  onError={handleOnError}
                  submitLabel={_s('pay')}
                  disclaimerLabel={_s(`${baseTranslationKey}.view.${view}.disclaimer`)}
                  amount={amount}
                  guestId={guestId}
                  isGiftcardCheckout={isGiftcardCheckout}
                />
              )}
            </>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
};

const CardsLoading = () => {
  return (
    <div className="py-md flex flex-col items-center justify-center">
      <LoadingIcon className="w-3xl" />
    </div>
  );
};

export default CreditCardModal;
